/**
 * Mock Auth Provider for development/testing
 *
 * This provider simulates Descope authentication for local development
 * without requiring real credentials. It is ONLY available in development
 * builds - the code is completely stripped from production bundles.
 *
 * Usage: Set VITE_MOCK_AUTH=true in your .env.local
 */

import { useCallback, useMemo } from 'react';
import { AuthContextProvider, type AuthContextValue } from './auth-context';

// Mock token for API calls - this is a fake JWT that won't work with real APIs
// but allows the UI to render without auth errors
const MOCK_TOKEN = 'mock-dev-token-not-for-production';

// Store mock token globally so getSessionToken can access it
if (typeof window !== 'undefined') {
  (window as unknown as { __MOCK_AUTH_TOKEN__?: string }).__MOCK_AUTH_TOKEN__ =
    MOCK_TOKEN;
}

interface MockAuthProviderProps {
  children: React.ReactNode;
}

export function MockAuthProvider({ children }: MockAuthProviderProps) {
  const logout = useCallback(async () => {
    console.log('[Mock Auth] Logout called - redirecting to login');
    // In mock mode, just redirect to login
    window.location.href = '/login';
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session: {
        isAuthenticated: true,
        isSessionLoading: false,
      },
      userState: {
        user: {
          userId: 'mock-user-123',
          email: 'dev@example.com',
          name: 'Dev User',
          picture: undefined,
        },
        isUserLoading: false,
      },
      actions: {
        logout,
      },
    }),
    [logout]
  );

  return <AuthContextProvider value={value}>{children}</AuthContextProvider>;
}

/**
 * Mock version of getSessionToken
 * Returns the mock token for API calls
 */
// eslint-disable-next-line react-refresh/only-export-components
export function getMockSessionToken(): string | undefined {
  if (typeof window !== 'undefined') {
    return (window as unknown as { __MOCK_AUTH_TOKEN__?: string })
      .__MOCK_AUTH_TOKEN__;
  }
  return MOCK_TOKEN;
}

/**
 * Check if mock auth is enabled (dev only)
 */
// eslint-disable-next-line react-refresh/only-export-components
export function isMockAuthEnabled(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_MOCK_AUTH === 'true';
}
